import { InterviewerPersona } from '@/components/admin/experiences/conversation-thread';
import {
  useVariableScope,
  useVariableSources,
} from '@/components/admin/experiences/experience-variable-scope';
import { AddBlockGrid } from '@/components/admin/experiences/page-editor/add-block-grid';
import { BlockInspector } from '@/components/admin/experiences/page-editor/block-inspector';
import { BlockStack } from '@/components/admin/experiences/page-editor/block-stack';
import {
  type InspectorTab,
  PAGE_EDITOR_IDS,
  PAGE_SELECTION,
  type PageSelection,
} from '@/components/admin/experiences/page-editor/contracts';
import { PageNav } from '@/components/admin/experiences/page-editor/page-nav';
import {
  type PageConversationProps,
  PagePanel,
} from '@/components/admin/experiences/page-editor/page-panel';
import { PagePreview } from '@/components/admin/experiences/page-editor/page-preview';
import { ProtocolPagePanel } from '@/components/admin/experiences/page-editor/protocol-page-panel';
import { StackGestures } from '@/components/admin/experiences/page-editor/stack-gestures';
import { TempsInspector } from '@/components/admin/experiences/page-editor/temps-inspector';
import { TempsTracks } from '@/components/admin/experiences/page-editor/temps-tracks';
import { ExperienceThemeBar } from '@/components/admin/experiences/theme/experience-theme-bar';
import { MediaPicker } from '@/components/admin/media-picker';
import { asStyle, readerThemeStyle } from '@/components/reader';
import { Callout } from '@/components/ui/callout';
import { blockCitationCount, convertBlock } from '@/lib/block-conversion';
import { conditionsMarkText, personNameHolder } from '@/lib/display-conditions';
import type { ExperienceDraft } from '@/lib/experience-draft';
import {
  draftAsContent,
  insertPageAt,
  insertedPageIndex,
  withNoyauTextsPin,
  withPageProtocol,
  withPageVisual,
} from '@/lib/experience-draft';
import { duplicateBlock, pasteBlock } from '@/lib/experience-duplicate';
import { type NoyauPreviewPair, type NoyauTextsSource, noyauTextsAt } from '@/lib/noyau-preview';
import {
  addBlock,
  addBlockToTemps,
  addTemps,
  addTempsAfter,
  duplicateVariableNames,
  entryOf,
  hasSeveralButtons,
  nextSelectedBlockId,
  pageNeighbours,
  removeBlock,
  removeTemps,
  replaceBlock,
  replaceTemps,
  setBlockTemps,
  withPage,
} from '@/lib/page-editor';
import type { PaletteContent } from '@/lib/variable-palette';
import { variablePalette } from '@/lib/variable-palette';
import { previewVariableNames, variableZoneOf } from '@/lib/variable-pill';
import type {
  AmbianceAsset,
  BlockType,
  ExperienceBlockDraft,
  ExperiencePageDraft,
  ProtocolCard,
  VariablePlace,
  VersionPin,
} from '@matter_extractor/shared';
import {
  EXPERIENCE_BLOCKS_MAX,
  EXPERIENCE_PAGES_MAX,
  conversationEligibilityOf,
  conversationRunOf,
  isConversationPage,
  noyauBlocksOf,
  resolveTheme,
} from '@matter_extractor/shared';
import { useMemo, useRef, useState } from 'react';

/**
 * Les trois zones de l'éditeur de page, plus le bandeau qui les surmonte
 * (refonte modulaire §9.3; planches `EditeurPageEcran` et
 * `EditeurPageTelephone`; issue #58).
 *
 * « Trois zones en grille 300 · 1fr · 340 : la pile à gauche, l'aperçu au
 * centre, l'inspecteur à droite. » This file owns the grid, the interface state
 * the three zones share, and nothing else: every edit is a call into
 * `lib/page-editor.ts`, every rendering of a page is `PageReader`.
 *
 * ## La sélection est projetée sur le brouillon à CHAQUE rendu
 *
 * The arbitration `protocoles/protocol-editor-fields.tsx` states for a step, met
 * again: a block removed by the trash, by a temps that merged, or by
 * « Repartir de cette version » would otherwise leave the inspector pointing at
 * nothing. So `shown` below resolves the selection against the page as it is
 * now, and falls back to the page panel - which is also what the maquette shows
 * when nothing is selected.
 *
 * ## Sous 768 px, l'aperçu passe SOUS la pile
 *
 * `admin-responsive.spec.ts` holds every administration screen to zero
 * horizontal overflow on a phone, and three columns of fixed width is exactly
 * what breaks that. The grid is one column below `lg`, in reading order - the
 * page and its stack, then the preview, then the inspector - because on a phone
 * one composes and checks, one does not watch while typing.
 *
 * ## La barre « Visuel de la page » s'intercale, et elle n'a pas d'état bloc
 *
 * §9.3 puts two banners above the three zones, and the bar of §9.4 is the
 * second: this file renders `PageNav` first for that reason. It is the very
 * component the Timeline mounts, at `level="page"` - `page.visual` written,
 * `draft.theme` inherited - so « hérité », « surchargé », « Revenir au global »
 * and the contrast warnings are the ones of issue #57, not a second wording.
 *
 * §9.4 left one question open: « reste à trancher si un bloc sélectionné la
 * fait passer à l'état bloc, ou si le visuel du bloc ne vit que dans l'onglet
 * Visuel de son inspecteur ». It is the SECOND, decided here and written into
 * §9.4: the bar stays at the page level whatever is selected. A third state
 * would put the three block tokens in two places at once - the bar spanning the
 * screen and the inspector's Visuel tab, thirty centimetres apart - and the two
 * would have to be kept in agreement by the operator's eye. The inspector also
 * wins on where the gesture already is: one selects a block in the stack or in
 * the preview, and the inspector is what opens beside both.
 *
 * ## La page protocole ne passe pas par les trois zones (§9.5)
 *
 * It holds no blocks, so a stack, an « Ajouter un texte » grid and an
 * interactive preview would each offer something the session cannot play. It
 * gets the same banner and the same bar - « le visuel de la page qui prend
 * celui du thème sauf surcharge » is §9.5 in as many words - then two columns:
 * the « Page » panel, and the card of the protocol it plays, with the Contexte
 * of D2 under it.
 *
 * ## Les variables suivent la POSITION de cette page (§8.6, D3 à D6)
 *
 * One zone per page: what each token IS here, and what the `@` menu offers
 * here, both derive from `{ kind: 'page', pageIndex }` - a field of THIS page
 * is out of reach on it, a field of a later page is counted in the menu's foot
 * rather than offered. The Contexte, edited on the protocol page, is its own
 * zone (`{ kind: 'context' }`): served while the protocol plays, it sees the
 * objectives that a page before the protocol does not. The protocol and the
 * criteria that name the two protocol kinds come from the screen's scope
 * (`experience-variable-scope.tsx`), read once above; what the previews NAME
 * derives from the same sources, so a pill and its preview say one name.
 *
 * ## « Jouer dans la conversation » vit ICI aussi (prolongation.md §3, §6 ; issue #186)
 *
 * Le panneau « Page » dessine la case et le contexte, ce corps les FAIT : cocher
 * pose `conversation` et retire la durée automatique, décocher le pose à `null` et
 * GARDE le texte du contexte dans l'état de ce corps - qui vit tant que la page est
 * ouverte, alors que le panneau, lui, se démonte dès qu'un bloc est choisi - pour
 * qu'un re-cochage le retrouve (§3.2). Le contexte est lu dans la portée de CETTE
 * page (`pageZone`, `pagePalette`) : le site `conversation-context` de `shared` y
 * place le texte comme un bloc de la page. La pile et l'aperçu reçoivent seulement
 * « cette page est du fil », et c'est le persona du brouillon que le fil de
 * l'aperçu nomme (`InterviewerPersona`, posé autour du corps).
 *
 * ## Les quatre gestes rapides de la pile vivent ICI (§9.3 ; issue #136)
 *
 * Insérer dans un temps nommé, changer le type d'un bloc, supprimer à la touche
 * ou à la croix, copier-coller entre pages : la pile et l'inspecteur les
 * OFFRENT, ce fichier les fait. La raison est celle que `BlockStack` énonce
 * déjà pour « Dupliquer » - la sélection et l'onglet de l'inspecteur sont deux
 * états d'ici, et le suffixe d'un nom de variable se calcule contre la timeline
 * entière, que seul le brouillon porte.
 *
 * Les touches et les événements de presse-papier sont ÉCOUTÉS par
 * `stack-gestures.tsx`, monté ici : la fenêtre est le seul endroit où un
 * `paste` arrive quand personne n'a le focus, et un `useEffect` ne peut pas
 * vivre après le retour anticipé ci-dessous. Ce fichier-ci ne fait que fournir
 * ce qu'il y a à faire quand un geste passe ses gardes.
 */

export interface PageEditorBodyProps {
  readonly draft: ExperienceDraft;
  readonly pageId: string;
  readonly readOnly: boolean;
  readonly format: 'phone' | 'screen';
  /** The media library, read by the caller; empty while it is in flight. */
  readonly assets: readonly AmbianceAsset[];
  /** The protocol library of §9.5, read by the caller only on the protocol page. */
  readonly protocols: readonly ProtocolCard[];
  /** Whether that read has landed - nothing is claimed before it (§4). */
  readonly protocolsRead: boolean;
  readonly onDraft: (next: ExperienceDraft) => void;
  readonly onGoToPage: (pageId: string) => void;
  readonly onUpload: (file: File, license: string, tags: string) => void;
  readonly uploading: boolean;
  /**
   * « Aperçu avec » (noyau.md N20): the pair of example the Noyau blocks of the
   * preview show. Held by the CALLER, above the key that remounts this body on
   * every page, so it survives a change of page and reaches the full-screen
   * preview of the head.
   */
  readonly noyauPair: NoyauPreviewPair;
  readonly onNoyauPair: (pair: NoyauPreviewPair) => void;
  /**
   * The texts of the noyaux, read by the caller (noyau.md §3.5, N20): the
   * inspector of a Noyau block names their versions, the preview renders the
   * real texts of the pair of example - under the tag of the experience's ONE
   * reference, which the body resolves (`noyauTextsAt`).
   */
  readonly noyauTexts: NoyauTextsSource;
}

/**
 * Le corps, sous le persona qui nomme la bulle d'un aperçu en fil : un composant
 * à part pour que ses deux sorties - la page protocole et les trois zones - le
 * partagent sans que l'une ni l'autre ait à le poser.
 */
export function PageEditorBody(props: PageEditorBodyProps) {
  return (
    <InterviewerPersona personaId={props.draft.persona?.ref.id ?? null}>
      <PageEditorBodyInner {...props} />
    </InterviewerPersona>
  );
}

function PageEditorBodyInner({
  draft,
  pageId,
  readOnly,
  format,
  assets,
  protocols,
  protocolsRead,
  onDraft,
  onGoToPage,
  onUpload,
  uploading,
  noyauPair,
  onNoyauPair,
  noyauTexts,
}: PageEditorBodyProps) {
  const [selection, setSelection] = useState<PageSelection>(PAGE_SELECTION);
  const [tab, setTab] = useState<InspectorTab>('contenu');
  const [tracks, setTracks] = useState(false);
  const [replayKey, setReplayKey] = useState(0);
  const [library, setLibrary] = useState(false);
  /** Le refus d'un collage au plafond - le seul geste de #136 qui échoue en silence sinon. */
  const [refusal, setRefusal] = useState<string | null>(null);
  /** Le texte du contexte d'une page DÉCOCHÉE, gardé pour un re-cochage (prolongation.md §3.2). */
  const [stashedContext, setStashedContext] = useState('');
  /** Le corps, pour savoir si une touche ou un collage nous est adressé - voir la tête. */
  const body = useRef<HTMLDivElement>(null);

  /*
    Les deux zones de variables de cet écran - see the head. Derived BEFORE the
    early return below, because a hook cannot sit after it; a page the timeline
    no longer holds has an index of -1, which nothing then reads.
  */
  const sources = useVariableSources(draft);
  const { experienceId } = useVariableScope();
  const pageIndex = draft.pages.findIndex((one) => one.id === pageId);
  const pagePlace = useMemo<VariablePlace>(() => ({ kind: 'page', pageIndex }), [pageIndex]);
  const pageZone = useMemo(
    () => variableZoneOf(sources, pagePlace, experienceId),
    [sources, pagePlace, experienceId],
  );
  const contextZone = useMemo(
    () => variableZoneOf(sources, { kind: 'context' }, experienceId),
    [sources, experienceId],
  );
  const variableNames = useMemo(() => previewVariableNames(sources), [sources]);
  const pagePalette = (query: string): PaletteContent => variablePalette(sources, pagePlace, query);
  const contextPalette = (query: string): PaletteContent =>
    variablePalette(sources, { kind: 'context' }, query);

  /*
    The texts of the noyaux (noyau.md §3.5, N20; #151), at the pin of the
    experience's ONE reference - `latest` by default: the inspector names their
    versions, the preview renders the real texts of the pair of example.
  */
  const noyauCount = noyauBlocksOf(draftAsContent(draft)).length;
  const noyauPin: VersionPin = draft.noyauTexts?.pin ?? 'latest';
  const texts = noyauTextsAt(noyauTexts, noyauPin);

  const page = draft.pages.find((one) => one.id === pageId) ?? null;
  if (page === null) {
    return (
      <Callout tone="warn">
        Cette page n’est plus dans la timeline de l’expérience. Elle a pu être supprimée depuis un
        autre onglet ; reviens à la Timeline pour en choisir une autre.
      </Callout>
    );
  }

  /**
   * Every edit of the page, written back into the experience's draft (§9.1).
   *
   * A `const` arrow and not a `function` declaration, and that is not a style:
   * a hoisted declaration could be called before the null check above, so
   * TypeScript refuses to narrow `page` inside it - and a closure over a `const`
   * created after the check keeps the narrowing.
   */
  const write = (next: ExperiencePageDraft): void => {
    onDraft(withPage(draft, pageId, () => next));
  };

  const replay = (): void => setReplayKey((was) => was + 1);

  /**
   * La case « Jouer dans la conversation » et son contexte (§3.1, §3.2).
   *
   * Cochée, la page perd sa durée automatique - le fil n'avance qu'au clic, et une
   * durée refusée par l'enregistrement serait un champ caché qu'on ne peut plus
   * vider. Décochée, `conversation` revient à `null` et le texte du contexte reste
   * ici. Une page protocole ou de retour n'offre la case que cochée : seule la
   * décocher a un sens.
   */
  const conversationPanel: PageConversationProps | undefined =
    page.kind === 'content' || page.conversation !== null
      ? {
          eligibility: conversationEligibilityOf(draft.pages, pageId),
          onToggle: (ticked) => {
            if (ticked) {
              write({
                ...page,
                autoDurationSeconds: null,
                conversation: { context: stashedContext },
              });
              return;
            }
            setStashedContext(page.conversation?.context ?? '');
            write({ ...page, conversation: null });
          },
          onContext: (context) => write({ ...page, conversation: { context } }),
          zone: pageZone,
          palette: pagePalette,
          // The last page of the thread, with a page behind it: the transition out of the thread (§3.3).
          keepsTransition:
            conversationRunOf(draft.pages).at(-1) === pageId && pageIndex < draft.pages.length - 1,
        }
      : undefined;

  /** The selection resolved against the page AS IT IS - see the head. */
  const entry = selection.kind === 'block' ? entryOf(page, selection.blockId) : null;
  const temps =
    selection.kind === 'temps'
      ? (page.temps.find((one) => one.id === selection.tempsId) ?? null)
      : null;
  const tempsIndex = temps === null ? -1 : page.temps.findIndex((one) => one.id === temps.id);

  const duplicates = duplicateVariableNames(draft);
  const neighbours = pageNeighbours(draft, pageId);

  /** Un bloc neuf : il prend la sélection et ouvre son inspecteur sur Contenu (§9.3). */
  const born = (blockId: string | null): void => {
    if (blockId === null) return;
    setSelection({ kind: 'block', blockId });
    setTab('contenu');
    setRefusal(null);
  };

  /**
   * Le « + » d'un séparateur : le bloc rejoint la fin de CE temps (§9.3, #136).
   *
   * C'est ce qui remplace « à la fin du dernier temps, puis glisse-le » : le
   * geste nomme son adresse au lieu de la déduire de la sélection.
   */
  const addToTemps = (tempsId: string, type: BlockType): void => {
    const { page: next, blockId } = addBlockToTemps(page, type, tempsId);
    write(next);
    born(blockId);
  };

  /**
   * « Ajouter un premier bloc » : la page sans aucun temps en sème un (§9.3).
   *
   * Le seul appelant restant de `addBlock`, et le seul cas où il n'y a pas de
   * temps à nommer - `addBlock` sème le sien, ce qu'`addBlockToTemps` ne peut
   * pas faire puisqu'il part d'un identifiant de temps.
   */
  const addFirstBlock = (type: BlockType): void => {
    const { page: next, blockId } = addBlock(page, type, null);
    write(next);
    born(blockId);
  };

  /**
   * « Dupliquer ce bloc » : la copie rejoint le temps de sa source, juste après
   * elle, PUIS prend la sélection et ouvre son inspecteur (§4.2).
   *
   * Le geste part du corps et non de la pile pour deux raisons que
   * `BlockStack` énonce sur sa prop : le suffixe du nom de variable se calcule
   * contre la timeline entière (§8.6), que seul le brouillon porte, et l'onglet
   * de l'inspecteur est un état d'ici. Un geste refusé - le plafond du temps -
   * rend le brouillon inchangé et ne déplace pas la sélection.
   */
  const duplicateOne = (blockId: string): void => {
    const next = duplicateBlock(draft, pageId, blockId);
    if (next.blockId === null) return;
    onDraft(next.draft);
    setSelection({ kind: 'block', blockId: next.blockId });
    setTab('contenu');
  };

  /**
   * « Ouvrir l'onglet Animation de ce bloc » : la pile appelle, le corps fait
   * les DEUX gestes d'un coup (§9.3 ; issue #139).
   *
   * Sélectionner et ouvrir un onglet sont deux états d'ici, et l'icône d'une
   * ligne ne peut en presser aucun - c'est le patron de `duplicateOne`
   * au-dessus, et la raison pour laquelle `BlockStack` ne reçoit qu'un seul
   * appel. Un Son y va aussi : son onglet Animation porte « Apparaît au
   * temps », qui reste un réglage même sans animation d'entrée.
   */
  const openAnimation = (blockId: string): void => {
    setSelection({ kind: 'block', blockId });
    setTab('animation');
  };

  /**
   * Changer le TYPE du bloc sélectionné (§9.3 ; issue #136).
   *
   * `convertBlock` garde l'identifiant, l'animation et le visuel, et fait
   * traverser les mots - le module dit tout ce qui passe et tout ce qui ne
   * passe pas. La sélection n'a donc rien à faire : elle pointe le même
   * identifiant, qui est toujours là.
   */
  const convertType = (type: BlockType): void => {
    if (entry === null) return;
    const next = convertBlock(entry.block, type);
    if (next === entry.block) return;
    write(replaceBlock(page, next));
    setRefusal(null);
  };

  /**
   * Supprimer un bloc, et POSER LA SÉLECTION SUIVANTE (§9.3 ; issue #136).
   *
   * Le suivant du même temps, sinon le précédent, sinon la page : c'est ce qui
   * rend « maintenir Suppr pour vider un temps » possible, au lieu de retomber
   * sur la page à chaque frappe. Le successeur se lit sur la page d'AVANT le
   * retrait, seule où le bloc a encore des voisins.
   *
   * Rien n'est confirmé, et c'est la contrepartie exacte de #135 : la pile
   * d'annulation rend le geste réversible, donc une boîte serait un péage sur
   * un geste qu'on répète.
   */
  const removeOne = (blockId: string): void => {
    const next = nextSelectedBlockId(page, blockId);
    write(removeBlock(page, blockId));
    setSelection(next === null ? PAGE_SELECTION : { kind: 'block', blockId: next });
    setRefusal(null);
  };

  /** Supprimer un temps - ses blocs remontent dans le voisin, `removeTemps` le fait. */
  const removeOneTemps = (tempsId: string): void => {
    write(removeTemps(page, tempsId));
    setSelection(PAGE_SELECTION);
    setRefusal(null);
  };

  /**
   * Coller le bloc que `stack-gestures.tsx` a sorti du presse-papier (§9.3).
   *
   * Il rejoint le temps du bloc SÉLECTIONNÉ, à sa fin ; un TEMPS sélectionné le
   * prend aussi - il est nommé, c'est tout ce qu'il faut - et rien de
   * sélectionné retombe sur le dernier temps, le même défaut qu'`addBlock`
   * avait, et pour la même raison : c'est le seul temps qu'une page vide de
   * sélection puisse nommer.
   *
   * `pasteBlock` frappe un identifiant neuf et suffixe le nom de variable
   * contre la timeline entière : le geste est un `duplicateBlock` dont la source
   * est ailleurs, et il partage sa règle plutôt que d'en écrire une seconde.
   * Au plafond du temps, il rend le brouillon INCHANGÉ et un identifiant nul,
   * et ce refus-là doit se dire : un geste au clavier qui ne produit rien et
   * n'explique rien se relit comme une panne.
   */
  const pasteIntoPage = (pasted: ExperienceBlockDraft): void => {
    const tempsId = entry?.tempsId ?? temps?.id ?? page.temps[page.temps.length - 1]?.id ?? null;
    if (tempsId === null) {
      setRefusal('Cette page ne porte aucun temps : ajoute un premier bloc avant d’en coller un.');
      return;
    }
    const next = pasteBlock(draft, pageId, tempsId, pasted);
    if (next.blockId === null) {
      setRefusal(
        `Ce temps porte déjà ${EXPERIENCE_BLOCKS_MAX} blocs, le maximum : rien n’a été collé. Sélectionne un bloc d’un autre temps, ou fais-y de la place.`,
      );
      return;
    }
    onDraft(next.draft);
    born(next.blockId);
  };

  /**
   * The selected block WHEN it is one of the two that carry a file.
   *
   * Narrowed once, into a `const`, rather than inside the JSX: `entry.block` is
   * a discriminated union read through two property accesses, and TypeScript
   * does not carry that narrowing into the spread below.
   */
  const mediaBlock =
    entry !== null && (entry.block.type === 'image' || entry.block.type === 'sound')
      ? entry.block
      : null;

  /**
   * Un « + » du bandeau : la page neuve s'intercale, puis S'OUVRE (§9.3).
   *
   * The gap is the rank on the « before » side and the rank plus one on the
   * « after » side, which is what makes « avant » on page 1 aim at gap 0 - the
   * one address `addPage` cannot reach. The id is read out of the NEW draft
   * rather than minted here, so `insertPageAt` stays the only place a page is
   * born; and the navigation is the banner's own `onGoToPage`, so the editor
   * lands on the page somebody just asked for rather than on the one they left.
   * A refused insertion (the ceiling) answers the draft unchanged and navigates
   * nowhere.
   */
  const insert = (side: 'before' | 'after'): void => {
    const at = side === 'before' ? neighbours.index : neighbours.index + 1;
    const next = insertPageAt(draft, at);
    if (next === draft) return;
    onDraft(next);
    /* Where the page really went: the gap AFTER the page that precedes the
       feedback page is clamped before it (§7.1), so `at` alone would name the
       feedback page itself. */
    const born = next.pages[insertedPageIndex(draft, at)];
    if (born !== undefined) onGoToPage(born.id);
  };

  /** Les deux bandeaux de §9.3, au-dessus des zones et communs aux deux sortes de page. */
  const banners = (
    <>
      <PageNav
        neighbours={neighbours}
        pages={draft.pages}
        pageId={pageId}
        feedback={page.kind === 'feedback'}
        transition={page.transition}
        theme={draft.theme}
        format={format}
        readOnly={readOnly}
        canInsert={draft.pages.length < EXPERIENCE_PAGES_MAX}
        onGo={onGoToPage}
        onInsert={insert}
        variableNames={variableNames}
      />

      {/* La barre de #57 à son état page - `level`, et rien d'autre à savoir. */}
      <ExperienceThemeBar
        level="page"
        value={page.visual}
        inherited={draft.theme}
        subject={page.name === '' ? undefined : page.name}
        readOnly={readOnly}
        onChange={(visual) => onDraft(withPageVisual(draft, pageId, visual))}
      />
    </>
  );

  if (page.kind === 'protocol') {
    return (
      <div className="space-y-4">
        {banners}

        <div className="grid items-start gap-4 lg:grid-cols-[300px_minmax(0,1fr)]">
          <PagePanel
            page={page}
            readOnly={readOnly}
            isLast={neighbours.next === null}
            showDuration={false}
            onChange={write}
            conversation={conversationPanel}
          />

          <ProtocolPagePanel
            page={page}
            protocols={protocols}
            protocolsRead={protocolsRead}
            readOnly={readOnly}
            onChoose={(protocolId) =>
              onDraft(
                withPageProtocol(
                  draft,
                  pageId,
                  // A new choice is `latest` (vitrine.md V16).
                  protocolId === null ? null : { id: protocolId, pin: 'latest' },
                ),
              )
            }
            onPin={(pin) =>
              page.protocol === null
                ? undefined
                : onDraft(withPageProtocol(draft, pageId, { id: page.protocol.id, pin }))
            }
            context={draft.context}
            onContext={(context) => onDraft({ ...draft, context })}
            zone={contextZone}
            palette={contextPalette}
          />
        </div>
      </div>
    );
  }

  return (
    <div ref={body} className="space-y-4">
      {banners}

      {/* Les touches et le presse-papier - il n'écoute que, voir son fichier. */}
      <StackGestures
        root={body}
        readOnly={readOnly}
        block={entry?.block ?? null}
        tempsId={temps?.id ?? null}
        onRemoveBlock={removeOne}
        onRemoveTemps={removeOneTemps}
        onPasteBlock={pasteIntoPage}
      />

      <div className="grid items-start gap-4 lg:grid-cols-[300px_minmax(0,1fr)_340px]">
        <div className="min-w-0 space-y-3">
          {entry === null && temps === null ? (
            <PagePanel
              page={page}
              readOnly={readOnly}
              isLast={neighbours.next === null}
              showDuration={page.kind !== 'feedback'}
              onChange={write}
              conversation={conversationPanel}
            />
          ) : null}

          <label className="flex items-center gap-2 text-gray-800 text-sm">
            <input
              type="checkbox"
              data-testid={PAGE_EDITOR_IDS.tracksToggle}
              checked={tracks}
              onChange={(event) => setTracks(event.currentTarget.checked)}
              className="size-4 accent-bleu-nuit"
            />
            <span className="font-semibold">Régler les temps et animations</span>
          </label>

          {tracks ? (
            <TempsTracks
              page={page}
              readOnly={readOnly}
              selectedBlockId={entry?.block.id ?? null}
              onSelectBlock={(blockId) => setSelection({ kind: 'block', blockId })}
              onPage={write}
              onReplay={replay}
            />
          ) : (
            <BlockStack
              page={page}
              selection={selection}
              readOnly={readOnly}
              onSelect={setSelection}
              onPage={write}
              onDuplicate={duplicateOne}
              onOpenAnimation={openAnimation}
              onAddBlock={addToTemps}
              onRemove={removeOne}
              sources={sources}
              conversation={isConversationPage(page)}
            />
          )}

          {/* Le refus d'un collage : annoncé, parce qu'un geste au clavier qui
              ne produit rien se relit sinon comme une panne (§9.3). */}
          {refusal === null ? null : (
            <Callout tone="warn" live="alert">
              {refusal}
            </Callout>
          )}

          <AddBlockGrid
            readOnly={readOnly}
            tempsCount={page.temps.length}
            onAddFirstBlock={addFirstBlock}
            onAddTemps={() => write(addTemps(page))}
          />
        </div>

        <PagePreview
          page={page}
          next={neighbours.next}
          theme={draft.theme}
          format={format}
          selectedBlockId={entry?.block.id ?? null}
          onSelectBlock={(blockId) => {
            setSelection({ kind: 'block', blockId });
            setTab('contenu');
          }}
          replayKey={replayKey}
          onReplay={replay}
          variableNames={variableNames}
          pages={draft.pages}
          sources={sources}
          conditionMark={(one) => conditionsMarkText(one, sources)}
          noyauTexts={texts}
          noyauPair={noyauPair}
        />

        <div className="min-w-0 rounded-md border-2 border-gray-200 bg-white p-3">
          {entry !== null ? (
            <BlockInspector
              page={page}
              block={entry.block}
              tempsId={entry.tempsId}
              tab={tab}
              readOnly={readOnly}
              palette={pagePalette}
              zone={pageZone}
              sources={sources}
              pageIndex={pageIndex}
              personNameHolder={personNameHolder(draft, entry.block.id)}
              noyau={{
                pin: noyauPin,
                blockCount: noyauCount,
                detail: noyauTexts.detail,
                pair: noyauPair,
                onPin: (pin) => onDraft(withNoyauTextsPin(draft, pin)),
                onPair: onNoyauPair,
              }}
              feedbackPage={page.kind === 'feedback'}
              previewStyle={asStyle(
                readerThemeStyle(resolveTheme(draft.theme, page.visual), format),
              )}
              duplicateName={
                'variableName' in entry.block &&
                entry.block.variableName !== '' &&
                duplicates.has(entry.block.variableName)
              }
              severalButtons={hasSeveralButtons(page)}
              lastTemps={entry.tempsIndex === page.temps.length - 1}
              citations={blockCitationCount(draft, entry.block.id)}
              assets={assets}
              onTab={setTab}
              onType={convertType}
              onOpenLibrary={() => setLibrary(true)}
              onOpenTemps={(tempsId) => setSelection({ kind: 'temps', tempsId })}
              onSetTemps={(tempsId) => write(setBlockTemps(page, entry.block.id, tempsId))}
              onChange={(block) => {
                write(replaceBlock(page, block));
                if (block.animation !== entry.block.animation) replay();
              }}
              /* Le même geste que la croix de la gouttière, donc la même suite :
                 la sélection descend au bloc suivant (§9.3, #136). */
              onRemove={() => removeOne(entry.block.id)}
            />
          ) : temps !== null ? (
            <TempsInspector
              page={page}
              temps={temps}
              index={tempsIndex}
              readOnly={readOnly}
              onTemps={(next) => write(replaceTemps(page, next))}
              onAddAfter={() => write(addTempsAfter(page, temps.id))}
              onRemove={() => removeOneTemps(temps.id)}
            />
          ) : (
            <p className="text-gray-600 text-sm leading-5">
              Rien de sélectionné. Clique un bloc de la pile - ou son visuel dans l’aperçu - pour
              l’inspecter, ou un séparateur pour régler son temps.
            </p>
          )}
        </div>
      </div>

      {library && mediaBlock !== null ? (
        <MediaPicker
          title={mediaBlock.type === 'image' ? 'Image du bloc' : 'Son du bloc'}
          kind={mediaBlock.type}
          assets={assets}
          selectedId={mediaBlock.assetId}
          emptyWording="Aucun fichier : le bloc ne montre rien."
          busy={uploading}
          onConfirm={(assetId) => {
            write(replaceBlock(page, { ...mediaBlock, assetId }));
            setLibrary(false);
          }}
          onClose={() => setLibrary(false)}
          onUpload={onUpload}
        />
      ) : null}
    </div>
  );
}
